import { useState } from "react";
import { Helmet } from "react-helmet";
import { Container } from "reactstrap";
import { useHistory } from "react-router-dom";
import toast from "react-hot-toast";

import { Heading } from "components/Heading";
import { PageHeadings, PageTitles } from "constants";
import { APP_ROUTES } from "helpers/routeHelpers";
import { ConfirmationModal } from "components/CustomModal";
import DataTable from "components/DataTable/DataTable";
import { Pagination } from "components/Pagination";
import { useLocalPaginatedRequest } from "hooks/useLocalPaginatedRequest";
import { convertDateTime, dateTimeFormat } from "utils/dateFormatUtils";
import {
    feedbackFormListing,
    updateFeedbackForm,
    deleteFeedbackForm,
} from "api/api.service";
import typography from "../typography";
import Header from "./Header";
import editIcon from "assets/images/app/common/pencil-icon.svg";
import deleteIcon from "assets/images/app/common/trash-icon.svg";

const statusFromFilter = (filterValue) => {
    switch (filterValue) {
        case 2:
            return "ACTIVE";
        case 3:
            return "INACTIVE";
        default:
            return null;
    }
};

export default function FeedbackFormsListing() {
    const history = useHistory();
    const [filters, setFilters] = useState({ status: 1 });
    const [isModalActive, setIsModalActive] = useState(false);
    const [selectedForm, setSelectedForm] = useState({});
    const [isDeleting, setIsDeleting] = useState(false);

    const { isFetching, page, total, data, request: refetch, handlePageClick, onChangePageSize, pageSize } =
        useLocalPaginatedRequest(
            feedbackFormListing,
            {
                ...(statusFromFilter(filters.status) && {
                    status: statusFromFilter(filters.status),
                }),
            },
            [filters.status]
        );

    const handleFilterChange = (value, type) => {
        setFilters({ ...filters, [type]: value });
    };

    const handleAdd = () => history.push(APP_ROUTES.ADD_FEEDBACK_FORM);
    const handleEdit = (id) => history.push(`${APP_ROUTES.EDIT_FEEDBACK_FORM}/${id}`);

    const handleSetActive = async (formData) => {
        try {
            await updateFeedbackForm({ id: formData.id, bodyData: { status: "ACTIVE" } });
            toast.success("Feedback form set as active");
            refetch();
        } catch (error) {
            console.error("Error activating feedback form:", error);
        }
    };

    const handleDeleteClick = (formData) => {
        setSelectedForm(formData);
        setIsModalActive(true);
    };

    const confirmDelete = async () => {
        try {
            setIsDeleting(true);
            await deleteFeedbackForm(selectedForm.id);
            toast.success("Feedback form deleted");
            refetch();
        } catch (error) {
            console.error("Error deleting feedback form:", error);
        } finally {
            setIsDeleting(false);
            setIsModalActive(false);
        }
    };

    return (
        <>
            <div className="page-content">
                <Helmet>
                    <title>{PageTitles.FEEDBACK_FORMS}</title>
                </Helmet>
                <Container fluid className="bls-feedback customer-management">
                    <Heading heading={PageHeadings.FEEDBACK_FORMS} />
                    <Header
                        statusFilterVal={filters.status}
                        filterUpdateHandler={handleFilterChange}
                        onAddFeedbackForm={handleAdd}
                    />
                    <DataTable
                        data={data}
                        loading={isFetching}
                        config={[
                            {
                                title: typography.STATUS,
                                render: (row) => (
                                    <div
                                        className={`notification-status-container ${
                                            row.status === "ACTIVE"
                                                ? "status-sent"
                                                : "status-pending"
                                        }`}
                                    >
                                        <span>{row.status}</span>
                                    </div>
                                ),
                            },
                            {
                                title: typography.QUESTION_COUNT,
                                render: (row) => (
                                    <div className="standard-typo-14-400">
                                        {row.questions?.length ?? 0}
                                    </div>
                                ),
                            },
                            {
                                title: typography.CREATED,
                                render: (row) => (
                                    <div className="standard-typo-14-400">
                                        {convertDateTime({
                                            date: row.createdAt,
                                            customFormat: dateTimeFormat.monthLetterDateFormat,
                                        }) || "-"}
                                    </div>
                                ),
                            },
                            {
                                title: typography.ACTION,
                                render: (row) => (
                                    <div className="d-flex align-items-center">
                                        <div
                                            className="view-form-icon-con"
                                            onClick={() => handleEdit(row.id)}
                                        >
                                            <img src={editIcon} alt="edit icon" role="button" />
                                        </div>
                                        <div
                                            className="view-form-icon-con"
                                            onClick={() => handleDeleteClick(row)}
                                        >
                                            <img src={deleteIcon} alt="delete icon" role="button" />
                                        </div>
                                        {row.status === "INACTIVE" && (
                                            <button
                                                className="feedback-set-active-btn"
                                                onClick={() => handleSetActive(row)}
                                            >
                                                {typography.SET_ACTIVE}
                                            </button>
                                        )}
                                    </div>
                                ),
                            },
                        ]}
                    />
                    <Pagination
                        onPageClick={handlePageClick}
                        currentPage={page}
                        totalData={total}
                        handleChange={(e) => onChangePageSize(e)}
                        rowLimit={pageSize}
                    />
                </Container>
            </div>
            <ConfirmationModal
                isOpen={isModalActive}
                modalType="DELETE_FEEDBACK_FORM"
                hasCustomConfig={true}
                heading={typography.DELETE_FEEDBACK_FORM_CONFIRMATION_MESSAGE}
                headingClassName="delete-notification-heading"
                size="md"
                onNo={() => setIsModalActive(false)}
                onYes={confirmDelete}
                customYesBtnText={isDeleting ? "Deleting..." : "Delete"}
                customNoBtnText="Cancel"
                customImageSrc={deleteIcon}
                imageClassName="delete-notification-image"
                yesButtonClassName="delete-notification-yes-button"
                noButtonClassName="delete-notification-no-button"
                buttonsContainerClassName="delete-notification-buttons-container"
                disabled={isDeleting}
            />
        </>
    );
}
